<script setup lang="ts">
  import { useQueryClient } from '@tanstack/vue-query';
  import { SpeedInsights } from '@vercel/speed-insights/vue';
  import { useColorMode, useDebounceFn, useDocumentVisibility } from '@vueuse/core';
  import { storeToRefs } from 'pinia';
  import {
    computed,
    nextTick,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    provide,
    readonly,
    ref,
    watch,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { toast, Toaster } from '@ui/components/sonner';
  import StuutLogo from '@ui/components/StuutLogo.vue';
  import { setDefaultTimezone } from '@ui/components/time';
  import { TooltipProvider } from '@ui/components/tooltip';
  import 'vue-sonner/style.css';

  import { authClient } from '@/api';
  import Confetti from '@/components/stuut/Confetti.vue';
  import TaskDrawer from '@/components/tasks/TaskDrawer.vue';
  import { useConfetti } from '@/composables/useConfetti';
  import { useSharedIdle } from '@/composables/useSharedIdle';
  import {
    useBusinessUnitsStore,
    useEmailSenders,
    useLauncherStore,
    useProfile,
    usePusher,
    useStatusCounts,
    useTaskCollectionStore,
  } from '@/store';

  import { identifyUser as identifyPosthogUser } from './plugins/posthog-plugin';

  const profileStore = useProfile();
  const businessUnitsStore = useBusinessUnitsStore();
  const router = useRouter();
  const route = useRoute();
  const pusher = usePusher();
  const taskCollectionStore = useTaskCollectionStore();
  const statusCounts = useStatusCounts();
  const emailSenderStore = useEmailSenders();
  const profile = computed(() => profileStore.profile);
  const launcherStore = useLauncherStore();
  const visibility = useDocumentVisibility();

  const { isGroupView, isIndividualTaskView, isTaskDrawerOpen } = storeToRefs(taskCollectionStore);

  const { isOpenSidepanel } = storeToRefs(launcherStore);

  const fetchingEmailSenders = ref(false);
  const queryClient = useQueryClient();
  const showUserOnboardingSheet = ref(profile.value?.onboarding_required);
  const isProcessingUpdates = ref(false);
  const isDrawerTransitioning = ref(false);
  const confettiRef = ref();
  const { setConfettiRef: setConfettiComposableRef } = useConfetti();

  // const mountOrgOnboardingSheet = computed(
  //   () =>
  //     profile.value.organization &&
  //     !profile.value.organization.is_onboarding_complete &&
  //     !profile.value.is_support_user &&
  //     !profile.value.organization.is_onboarding_disabled,
  // );

  const openUserOnboardingSheet = () => {
    showUserOnboardingSheet.value = true;
  };

  const setupEmailSenders = async () => {
    if (fetchingEmailSenders.value) return;

    fetchingEmailSenders.value = true;

    try {
      return await emailSenderStore.fetchEmailSenders();
    } catch (err: any) {
      toast.error(err.clientMessage);
    } finally {
      fetchingEmailSenders.value = false;
    }
  };

  const forceTaskTableRefresh = () => {
    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasks'],
    });
  };

  const forceTaskDrawerTableRefresh = () => {
    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasksDrawerTable'],
    });
  };

  const forceAllTaskTableRefresh = () => {
    forceTaskDrawerTableRefresh();
    forceTaskTableRefresh();
  };

  provide('forceAllTaskTableRefresh', forceAllTaskTableRefresh);
  provide('forceTaskTableRefresh', forceTaskTableRefresh);
  provide('forceTaskDrawerTableRefresh', forceTaskDrawerTableRefresh);
  provide('isDrawerOpen', readonly(isTaskDrawerOpen));
  provide('openTaskDrawer', taskCollectionStore.openTaskDrawer);
  provide('closeDrawer', taskCollectionStore.closeTaskDrawer);

  const setupConfettiTrigger = () => {
    if (confettiRef.value?.triggerConfetti) {
      setConfettiComposableRef(confettiRef.value);
    } else {
      setTimeout(setupConfettiTrigger, 100);
    }
  };

  onBeforeMount(async () => {
    // App.vue mounts on every route, including the unauthenticated sign-in
    // page. Skip the profile fetch when there's no session — otherwise it 401s
    // and the API client's interceptor fires a pointless /auth/logout.
    if (!authClient.hasAuthToken && !authClient.hasRefreshToken) return;

    await profileStore.fetchProfile(true);

    if (profile.value.id) {
      try {
        pusher.initialize();
        identifyPosthogUser(profile.value);
        launcherStore.verifyChatEnabled(profile.value);
        setupEmailSenders();
        statusCounts.fetchAll();
        if (profileStore.hasBusinessUnits) businessUnitsStore.fetch();
      } catch (err: any) {
        toast.error(err.clientMessage);
      }
    }
  });

  onMounted(() =>
    nextTick(() => {
      setupConfettiTrigger();

      if (process.env.STUUT_ENV === 'local') {
        document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]').forEach((link) => {
          link.href = link.href.replace(/favicon/, 'favicon-dev');
        });
      }
    }),
  );

  const handleTaskDrawerUpdates = useDebounceFn(async () => {
    if (isProcessingUpdates.value || isDrawerTransitioning.value) return;

    try {
      isProcessingUpdates.value = true;
      taskCollectionStore.isLoadingTask = true;

      taskCollectionStore.openTaskDrawer({
        task: taskCollectionStore.currentTask,
        taskId: (route.query.task_drawer_id as string) || taskCollectionStore.selectedTaskId,
      });

      const results = await taskCollectionStore.buildCollectionState();

      if (!results) return;

      taskCollectionStore.$patch(results?.state as any);
      taskCollectionStore.refreshTaskDrawerTable();

      isDrawerTransitioning.value = true;

      await router.push({
        query: {
          ...route.query,
          ...results?.queryParams,
        },
      });
    } catch (err: any) {
      console.error(err.clientMessage);
    } finally {
      nextTick(() => {
        isDrawerTransitioning.value = false;
        isProcessingUpdates.value = false;
        taskCollectionStore.isLoadingTask = false;
      });
    }
  }, 150);

  // watch(
  //   () => route.query.group_view_mode,
  //   async () => {
  //     if (isGroupView.value as boolean) {
  //       taskCollectionStore.setGroupViewModeOptions(
  //         route.query.group_view_mode as string,
  //       );
  //     }
  //   },
  // );

  watch(
    () => route.query,
    async (currQPs, oldQPs) => {
      // Skip route processing during login/authentication
      if (!profile.value?.id) return;

      if (isShouldCloseTaskDrawer()) {
        return taskCollectionStore.closeTaskDrawer();
      }

      if (isProcessingUpdates.value || isDrawerTransitioning.value) return;

      if (taskCollectionStore.hasValidTaskDrawerChanges(currQPs, oldQPs)) {
        handleTaskDrawerUpdates();
      }
    },
  );

  const isShouldCloseTaskDrawer = () => {
    const isGroup = taskCollectionStore.getIsGroupView();

    if (isGroup) {
      return !route.query.task_drawer_id && !route.query.group_view_id;
    }

    return !isGroup && !route.query.task_drawer_id;
  };

  watch(
    profile,
    async (current, old) => {
      if (current.organization) {
        if (!old?.organization) {
          try {
            pusher.initialize();
            identifyPosthogUser(profile.value);
            launcherStore.verifyChatEnabled(profile.value);
          } catch (error) {
            console.error(error);
          }
        }
        setupEmailSenders();

        setDefaultTimezone(current.organization.timezone);
        launcherStore.verifyChatEnabled(profile.value);

        if (!showUserOnboardingSheet.value && current.onboarding_required) {
          openUserOnboardingSheet();
        }
      }
    },
    { deep: true, immediate: true },
  );

  watch(
    () => route.meta.statusCountType,
    (type) => {
      statusCounts.activeCountType = type ?? null;
      if (type) statusCounts.markSeen(type);
    },
    { immediate: true },
  );

  const stopVisibilityWatch = watch(visibility, (current) => {
    if (current === 'visible') {
      statusCounts.fetchAll();
    }
  });

  // Sign the user out after a period of inactivity so an unattended screen
  // doesn't leave sensitive content exposed. Idle expiry from token timeout is
  // already handled reactively by the API client's 401 interceptor.
  //
  // Idle state is shared across tabs so activity in any open tab keeps the
  // whole session alive — we only sign out once every tab has gone quiet.
  const IDLE_TIMEOUT_MS = (Number(process.env.STUUT_IDLE_TIMEOUT_MINUTES) || 30) * 60 * 1000;
  const { idle, clear: clearSharedIdle } = useSharedIdle(IDLE_TIMEOUT_MS);

  const stopIdleWatch = watch(idle, (isIdle) => {
    if (isIdle && (authClient.hasAuthToken || authClient.hasRefreshToken)) {
      // Stop watching before logout: idle flips back to false on the redirect's
      // own activity and would otherwise re-arm during teardown.
      stopIdleWatch();
      clearSharedIdle();
      // Each tab passes its own current page so that after re-login the user
      // lands back where that tab was (sign-in honors the `redirect` param).
      authClient.logout(true, route.fullPath);
    }
  });

  onBeforeUnmount(() => {
    stopVisibilityWatch();
    stopIdleWatch();
  });

  const mode = useColorMode({
    attribute: 'data-theme',
    initialValue: 'light',
    modes: {
      dark: 'dark',
      light: 'light',
    },
  });
</script>

<template>
  <TooltipProvider :skip-delay-duration="250">
    <main
      id="page"
      class="h-screen w-screen relative"
      :class="{ 'pr-[600px] overflow-x-auto': isOpenSidepanel }"
    >
      <div id="content" class="h-screen w-screen xs:flex hidden">
        <Confetti
          ref="confettiRef"
          :destroy-target="false"
          :particle-count="100"
          :explosion-power="30"
        >
          <div
            style="position: fixed; top: 0; left: 0; width: 1px; height: 1px; visibility: hidden"
          />
        </Confetti>

        <router-view />
      </div>

      <div
        class="h-screen w-screen xs:hidden flex flex-col gap-4 items-center text-center text-sm justify-center px-5"
      >
        <StuutLogo />

        <div>Stuut is not supported on smaller devices.</div>

        <div>Try visiting from a desktop device for a better experience.</div>
      </div>
    </main>

    <TaskDrawer />

    <SpeedInsights />

    <!--    TODO: deprecate or bring back to life the onboardings below ? -->

    <!--  <UserOnboardingSheet -->
    <!--    :show="showOnboardingSheet" -->
    <!--    @open="openOnboardingSheet" -->
    <!--    @close="closeOnboardingSheet" -->
    <!--  /> -->

    <!--  <OrganizationOnboardingSheet v-if="mountOrgOnboardingSheet" /> -->

    <Toaster
      close-button
      class="pointer-events-auto"
      :data-theme="mode"
      :data-sonner-theme="mode"
    />
  </TooltipProvider>
</template>

<style scoped lang="postcss"></style>
